import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { remoteConfig } from '../../utility/firebaseRemoteConfigHelper';
import { db } from '../../auth/firebaseConfig';
import {
  getHicpForDate,
  calculateRentIncrease
} from '../../utility/rentReviewCalculator';
import { subtractMonthsSafe } from '../../utility/dateHelpers';
import {
  CalculatorIcon,
  CalendarDaysIcon,
  CurrencyEuroIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';

interface FormValues {
  currentRent: number;
  lastSetDate: string; // yyyy-mm-dd
  newSetDate: string; // yyyy-mm-dd
}

interface RentReviewCalculatorProps {
  onCalculationComplete?: () => void;
}

const RentReviewCalculator: React.FC<RentReviewCalculatorProps> = ({
  onCalculationComplete,
}) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      currentRent: 1000,
      lastSetDate: '2023-06-05',
      newSetDate: new Date().toISOString().split('T')[0] // Default to today
    }
  });

  const [capPercent, setCapPercent] = useState<number>(2);
  const [result, setResult] = useState<null | {
    oldHicp: number;
    newHicp: number;
    hicpChangePercent: number;
    capPercentApplied: number;
    appliedPercent: number;
    increaseAmount: number;
    input: { currentRent: number; lastSetDate: Date; newSetDate: Date };
  }>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      await remoteConfig.ensureInitialized();
      setCapPercent(remoteConfig.getRpzAnnualRentCapPercentage());
    })();
  }, []);

  const onSubmit = async (values: FormValues) => {
    setError(null);
    setResult(null);
    try {
      // Raw dates entered by user
      const rawNewDate = new Date(values.newSetDate);
      const rawLastDate = new Date(values.lastSetDate);

      // Adjusted dates for HICP lookup: subtract 2 months (always)
      const adjNewDate = subtractMonthsSafe(rawNewDate, 2);
      const adjLastDate = subtractMonthsSafe(rawLastDate, 2);

      const oldHicp = await getHicpForDate(db, adjLastDate);
      const newHicp = await getHicpForDate(db, adjNewDate);

      if (oldHicp == null || newHicp == null) {
        setError(
          'HICP values not found for one or both dates. Please ensure dates are within valid ranges.'
        );
        return;
      }

      const calc = calculateRentIncrease(
        {
          currentRent: values.currentRent,
          lastSetDate: rawLastDate,
          newSetDate: rawNewDate,
          capPercentPerYear: capPercent
        },
        oldHicp,
        newHicp
      );

      // If the computed increase is negative, clamp to zero and normalize percent
      const normalizedCalc =
        calc.increaseAmount < 0
          ? {
              ...calc,
              increaseAmount: 0,
              appliedPercent: Math.max(0, calc.appliedPercent)
            }
          : calc;

      setResult({
        ...normalizedCalc,
        input: {
          currentRent: values.currentRent,
          lastSetDate: rawLastDate,
          newSetDate: rawNewDate
        }
      });
      onCalculationComplete?.();
    } catch (e: any) {
      const msg =
        typeof e?.message === 'string' ? e.message : 'Unexpected error';
      if (msg.includes('Missing or insufficient permissions')) {
        setError(
          'Unable to read HICP data (permission denied). Please sign in.'
        );
      } else {
        setError(msg);
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mt-8">
      {/* Header */}
      <div className="bg-slate-50 border-b border-gray-100 p-6 flex items-center gap-3">
        <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
          <CalculatorIcon className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            RPZ Rent Calculator
          </h2>
          <p className="text-sm text-gray-500">
            Calculate permissible rent increases in Rent Pressure Zones
          </p>
        </div>
      </div>

      <div className="p-6">
        {/* Input Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Rent Amount
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <CurrencyEuroIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="number"
                step="1"
                {...register('currentRent', { valueAsNumber: true })}
                className="block w-full rounded-lg border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500 py-2.5 border"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Rent Last Set
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <CalendarDaysIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="date"
                  {...register('lastSetDate')}
                  className="block w-full rounded-lg border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500 py-2.5 border"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Rent Date
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <CalendarDaysIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="date"
                  {...register('newSetDate')}
                  className="block w-full rounded-lg border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500 py-2.5 border"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:bg-blue-300"
          >
            {isSubmitting ? 'Calculating...' : 'Calculate Maximum Rent'}
          </button>
        </form>

        {error && (
          <div className="mt-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon
                  className="h-5 w-5 text-red-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Calculation Error
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Primary Result Card */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 mb-6">
              <div className="text-center">
                <p className="text-sm font-medium text-emerald-800 uppercase tracking-wide">
                  Maximum Permitted Rent
                </p>
                <div className="mt-2 flex items-baseline justify-center gap-2">
                  <span className="text-4xl font-extrabold text-emerald-700">
                    €
                    {Math.round(
                      result.input.currentRent + result.increaseAmount
                    ).toLocaleString()}
                  </span>
                  <span className="text-sm font-medium text-emerald-600">
                    / month
                  </span>
                </div>
                {result.increaseAmount > 0 ? (
                  <div className="mt-2 inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                    <ArrowTrendingUpIcon className="w-3 h-3 mr-1" />
                    Increase of €{Math.round(result.increaseAmount)} allowed
                  </div>
                ) : (
                  <div className="mt-2 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                    <InformationCircleIcon className="w-3 h-3 mr-1" />
                    No increase permitted
                  </div>
                )}
              </div>
            </div>

            {/* Calculation Breakdown */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                <ScaleIcon className="w-4 h-4 mr-2 text-gray-500" />
                Calculation Logic
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {/* HICP Box */}
                <div
                  className={`p-3 rounded-lg border ${result.appliedPercent === result.hicpChangePercent ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-300' : 'bg-white border-gray-200 opacity-75'}`}
                >
                  <span className="text-xs text-gray-500 block">
                    Inflation (HICP)
                  </span>
                  <div className="font-semibold text-lg text-gray-900">
                    {result.hicpChangePercent.toFixed(2)}%
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {result.oldHicp.toFixed(1)} → {result.newHicp.toFixed(1)}
                  </div>
                </div>

                {/* RPZ Cap Box */}
                <div
                  className={`p-3 rounded-lg border ${result.appliedPercent === result.capPercentApplied ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-300' : 'bg-white border-gray-200 opacity-75'}`}
                >
                  <span className="text-xs text-gray-500 block">
                    RPZ Cap Limit
                  </span>
                  <div className="font-semibold text-lg text-gray-900">
                    {result.capPercentApplied.toFixed(2)}%
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {capPercent}% per annum
                  </div>
                </div>
              </div>

              <div className="mt-4 text-xs text-center text-gray-500">
                Formula Applied: €{result.input.currentRent} ×{' '}
                {result.appliedPercent.toFixed(2)}% ={' '}
                <strong>€{Math.round(result.increaseAmount)}</strong>
              </div>
            </div>

            {/* Legal / Disclaimers */}
            <div className="mt-6 space-y-3">
              <p className="text-xs text-gray-400 text-center">
                This tool provides an indicative calculation. Always verify with
                the
                <a
                  href="https://rtb.ie/compliance/check-rpz-compliance/rpz-calculator/"
                  className="text-blue-600 hover:underline mx-1"
                >
                  Official RTB Calculator
                </a>
                and
                <a
                  href="https://ec.europa.eu/eurostat/databrowser/explore/all/economy"
                  className="text-blue-600 hover:underline mx-1"
                >
                  Eurostat Data
                </a>
                .
              </p>

              {/* Legal Site Disclaimer */}
              <p className="text-[11px] leading-relaxed text-gray-500 text-center mt-2">
                The information provided by RentSmart.ie is for general guidance
                only and does not constitute legal, financial, or professional
                advice. While we strive for accuracy, no warranty is given and
                RentSmart.ie, its owners, or affiliates accept no liability for
                errors, omissions, or any loss arising from reliance on this
                tool. Always confirm outcomes with official sources and seek
                independent professional advice where appropriate. By using this
                feature you agree to our
                <a href="/terms" className="text-blue-600 hover:underline mx-1">
                  Terms of Use
                </a>
                and
                <a
                  href="/privacy"
                  className="text-blue-600 hover:underline mx-1"
                >
                  Privacy Policy
                </a>
                .
              </p>

              {/* RPZ Guidance Notes */}
              <div className="mt-3 text-xs bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-indigo-900">
                <p className="font-semibold flex items-center gap-2">
                  <InformationCircleIcon className="w-4 h-4 text-indigo-600" />
                  RPZ Guidance
                </p>
                <div className="mt-2 bg-indigo-100/60 border border-indigo-200 rounded-md p-2 text-indigo-900">
                  <p className="font-medium">RPZ Rule Note</p>
                  <p className="mt-1">
                    If you are in a new RPZ area (designated in the last two
                    years), your next rent review can only take place{' '}
                    <strong>24 months</strong> after your last review.
                  </p>
                  <p className="mt-2">
                    <a
                      href="https://rtb.ie/compliance/check-rpz-compliance/how-rent-pressure-zones-rpzs-work/"
                      className="text-indigo-700 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Read more about how RPZs work →
                    </a>
                  </p>
                </div>
                <p className="mt-1">
                  If your tenancy existed before the area was designated as a
                  Rent Pressure Zone (RPZ), the landlord must wait for a full
                  24-month period (from the start of the tenancy or the last
                  rent review) before conducting the <em>first</em> review under
                  RPZ rules.
                </p>
                <div className="mt-2 space-y-1">
                  <p className="font-medium">1) First Review (Transition)</p>
                  <p>
                    • Requirement: No rent review until{' '}
                    <strong>24 months</strong> have elapsed since the rent was
                    last set.
                  </p>
                  <p>
                    • Limit: When that review occurs, the increase is capped by
                    the RPZ formula —
                    <strong> up to 2% per year pro‑rata</strong> or the rate of
                    inflation (<strong>HICP</strong>), whichever is lower.
                  </p>
                </div>
                <div className="mt-2 space-y-1">
                  <p className="font-medium">
                    2) Subsequent Reviews (New Cycle)
                  </p>
                  <p>
                    • Frequency: After the first RPZ review, future reviews may
                    occur <strong>every 12 months</strong>.
                  </p>
                  <p>
                    • Caps: All subsequent reviews remain subject to RPZ price
                    caps (2%/year pro‑rata or HICP, lower applies).
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentReviewCalculator;
